import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

function clampNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

/**
 * Builds per-BlockCounter daily totals suitable for heatmap rendering.
 *
 * We rely on tracking_elements snapshots, so this works for Reports v2.
 *
 * Rows we consider:
 * - block_type: 'BlockCounter'
 * - element_type: 'counter_inc' (and optionally 'counter_dec')
 *
 * @param {Array<object>} rows tracking_elements rows
 * @param {number} fromTs ms
 * @param {number} toTs ms
 * @param {string} tz IANA timezone (e.g. 'Europe/Prague')
 * @returns {Array<object>} heatmap models
 */
export function buildCounterHeatmaps(rows, fromTs, toTs, tz, goalByBlockId = {}) {
  const from = dayjs(fromTs).tz(tz).startOf('day');
  const to = dayjs(toTs).tz(tz).endOf('day');

  const byBlockId = new Map();

  (rows || []).forEach((row) => {
    if (!row) return;
    if (row.block_type !== 'BlockCounter') return;

    const type = row.element_type;
    const isCounterEvent = type === 'counter_inc' || type === 'counter_dec';
    if (!isCounterEvent) return;

    const start = clampNumber(row.start_ts, null);
    if (!start) return;

    // Range clamp (the Supabase helper already overlaps, but this keeps it robust)
    if (start < from.valueOf() || start > to.valueOf()) return;

    const blockId = row.block_id || 'unknown';
    const title = row.block_title || 'Untitled';
    const color = row.block_color || 'gray';
    const faceType = row.block_face_type || 'default';

    const dateKey = dayjs(start).tz(tz).format('YYYY-MM-DD');

    const delta = clampNumber(row.value, 1) * (type === 'counter_dec' ? -1 : 1);

    if (!byBlockId.has(blockId)) {
      byBlockId.set(blockId, {
        blockId,
        title,
        color,
        faceType,
        totalsByDate: {},
        goal: null,
        completedByDate: {},
        totalAll: 0,
        maxDay: 0,
      });
    }

    const item = byBlockId.get(blockId);
    item.totalsByDate[dateKey] = clampNumber(item.totalsByDate[dateKey], 0) + delta;
    item.totalAll += delta;
    item.maxDay = Math.max(item.maxDay, item.totalsByDate[dateKey]);

    const goalFromMeta = row.meta && typeof row.meta.size !== 'undefined' ? clampNumber(row.meta.size, null) : null;
    if (goalFromMeta !== null && goalFromMeta > 0) {
      item.goal = goalFromMeta;
    }
  });

  // Derive completedByDate (binary)
  Array.from(byBlockId.values()).forEach((item) => {
    if ((item.goal === null || item.goal <= 0) && goalByBlockId && item.blockId && goalByBlockId[item.blockId]) {
      const goalFromBlocks = clampNumber(goalByBlockId[item.blockId], null);
      if (goalFromBlocks !== null && goalFromBlocks > 0) {
        item.goal = goalFromBlocks;
      }
    }

    const goal = clampNumber(item.goal, null);
    Object.keys(item.totalsByDate).forEach((dateKey) => {
      const total = clampNumber(item.totalsByDate[dateKey], 0);
      // If goal is unknown, treat any activity as completed
      const completed = goal && goal > 0 ? total >= goal : total > 0;
      item.completedByDate[dateKey] = completed;
    });
  });

  return Array.from(byBlockId.values())
    .filter((b) => {
      // Keep only counters with any activity in range
      return Object.keys(b.totalsByDate).some((k) => clampNumber(b.totalsByDate[k], 0) !== 0);
    })
    .sort((a, b) => {
      // Desc by activity, then alpha
      if (b.totalAll !== a.totalAll) return b.totalAll - a.totalAll;
      return String(a.title).localeCompare(String(b.title));
    });
}
